import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBag, ArrowLeft, Star, CheckCircle, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import { SEO } from '../components/SEO';


const FAQItem = ({ question, answer }: { question: string, answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button 
        className="w-full py-4 flex justify-between items-center text-left focus:outline-none group"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-display font-bold text-simba-dark text-lg group-hover:text-simba-green transition-colors">{question}</span>
        {isOpen ? <ChevronUp className="text-simba-gold" /> : <ChevronDown className="text-gray-400 group-hover:text-simba-gold" />}
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
        <p className="text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

export const ProductDetail = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const { products, loading } = useProducts();
  
  const product = products.find(p => p.slug === slug);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Set initial selected image when product loads
  React.useEffect(() => {
    if (product) {
      setSelectedImage(product.image);
    }
  }, [product]);

  if (loading) {
     return (
        <div className="h-screen flex items-center justify-center text-simba-green">
           <Loader className="animate-spin mr-2" /> Loading Details...
        </div>
     );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-32 pb-20 text-center flex flex-col items-center justify-center">
        <SEO title="Product Not Found" description="The product you are looking for does not exist or has been removed. Browse our natural remedies at Simba Herbals UK." canonical="/shop" />
        <h2 className="text-3xl font-bold mb-4 text-simba-dark font-display">Product Not Found</h2>
        <p className="text-gray-500 mb-8">The product you are looking for does not exist or has been removed.</p>
        <Link to="/shop" className="px-8 py-3 bg-simba-green text-white rounded-full font-bold hover:bg-simba-gold transition-colors">
           Back to Shop
        </Link>
      </div>
    );
  }

  const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.simbaherbals.com';
  const productImage = product.image.startsWith('http') ? product.image : `${SITE_URL}${product.image}`;
  const productUrl = `${SITE_URL}/product/${product.slug}`;
  const metaDesc = product.description.length > 155 ? product.description.slice(0, 152) + '...' : product.description;
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: productImage,
    url: productUrl,
    brand: { '@type': 'Brand', name: 'Simba Herbals UK' },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
      url: productUrl,
    },
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Shop', item: `${SITE_URL}/shop` },
      { '@type': 'ListItem', position: 3, name: product.name, item: productUrl },
    ],
  };

  return (
    <div className="pb-20 bg-white">
      <SEO
        title={product.name}
        description={metaDesc}
        canonical={`/product/${product.slug}`}
        image={productImage}
        type="product"
        jsonLd={[productJsonLd, breadcrumbJsonLd]}
      />
      {/* Mini Breadcrumb/Header */}
      {/* Mini Breadcrumb/Header */}
      <div className="bg-simba-dark min-h-screen flex flex-col justify-center px-6 lg:px-12 text-white relative">
        <div className="absolute inset-0 bg-gradient-to-br from-simba-green/10 to-transparent pointer-events-none"></div>
        <div className="max-w-[1400px] mx-auto w-full pt-[200px]">
             <Link to="/shop" className="inline-flex items-center text-white mb-6 hover:text-simba-gold transition-colors text-sm font-bold uppercase tracking-widest">
                <ArrowLeft size={16} className="mr-2" /> Back to Shop
             </Link>
             <h1 className="font-display text-5xl lg:text-7xl leading-tight mb-6">{product.name}</h1>
             <p className="text-xl text-gray-400 max-w-2xl leading-relaxed">{product.description}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Left: Image Gallery */}
            <div className="space-y-6">
                <div className="aspect-square bg-simba-cream rounded-3xl overflow-hidden relative group">
                    <img 
                      src={selectedImage || product.image} 
                      alt={product.name} 
                      className="w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-700"
                      referrerPolicy="no-referrer"
                    />
                    {product.tag && (
                      <div className="absolute top-6 left-6 bg-simba-green text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wider shadow-lg">
                        {product.tag}
                      </div>
                    )}
                </div>
                
                {/* Thumbnails */}
                {product.images && product.images.length > 1 && (
                  <div className="grid grid-cols-5 gap-4">
                    {product.images.map((img, index) => (
                      <button 
                        key={index}
                        onClick={() => setSelectedImage(img)}
                        className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${selectedImage === img ? 'border-simba-gold header-simba-green' : 'border-transparent hover:border-simba-green/30'}`}
                      >
                        <img 
                          src={img} 
                          alt={`${product.name} ${index + 1}`} 
                          className="w-full h-full object-cover" 
                          referrerPolicy="no-referrer"
                        />
                      </button>
                    ))}
                  </div>
                )}
            </div>

            {/* Right: Info & Purchase */}
            <div>
               <div className="flex items-center space-x-2 mb-6">
                  <div className="flex text-simba-gold">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={18} fill={i < product.rating ? "currentColor" : "none"} />
                    ))}
                  </div>
                  <span className="text-sm text-gray-400 font-medium">({product.reviews} verified reviews)</span>
               </div>

               <div className="flex items-end gap-4 mb-8">
                  <span className="font-display font-bold text-4xl text-simba-dark">£{product.price.toFixed(2)}</span>
                  {product.originalPrice > product.price && (
                    <span className="text-xl text-gray-400 line-through mb-1">£{product.originalPrice.toFixed(2)}</span>
                  )}
               </div>

               <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {product.description}
               </p>

               <button 
                  onClick={() => addToCart(product)}
                  className="w-full bg-simba-green text-white py-4 rounded-xl font-bold hover:bg-simba-gold transition-colors shadow-lg flex items-center justify-center text-lg mb-8 transform hover:-translate-y-1"
               >
                  Add to Cart <ShoppingBag className="ml-2" />
               </button>

               <div className="border-t border-gray-100 pt-8 space-y-4">
                  <div className="flex items-center text-sm text-gray-500">
                     <CheckCircle size={16} className="text-simba-green mr-2" />
                     <span>Discreet Packaging</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                     <CheckCircle size={16} className="text-simba-green mr-2" />
                     <span>Next Day Delivery Available</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                     <CheckCircle size={16} className="text-simba-green mr-2" />
                     <span>100% Authentic African Herbs</span>
                  </div>
               </div>

               <div className="mt-8 border-t border-gray-100 pt-8">
                  <h3 className="font-display text-xl text-simba-dark mb-4">Product Description</h3>
                  <div className="prose prose-sm text-gray-600">
                     {product.longDescription.split('\n\n').map((paragraph, i) => (
                        <p key={i} className="mb-3">{paragraph}</p>
                     ))}
                  </div>
               </div>
            </div>
         </div>

         {/* Detailed Content Tabs/Sections */}
         <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
            
            {/* Main Description */}
            <div className="lg:col-span-7 space-y-12">


               {product.usage && (
                 <section className="bg-simba-cream p-8 rounded-3xl border border-simba-green/10">
                    <h2 className="font-display text-2xl text-simba-dark mb-6">{product.usage.title}</h2>
                    <ul className="space-y-4">
                       {product.usage.steps.map((step, i) => (
                          <li key={i} className="flex items-start">
                             <div className="w-8 h-8 bg-simba-gold text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 mt-0.5 text-sm">
                                {i + 1}
                             </div>
                             <p className="ml-4 text-gray-700">{step}</p>
                          </li>
                       ))}
                    </ul>
                 </section>
               )}
            </div>

            {/* FAQs */}
            <div className="lg:col-span-5">
               <h2 className="font-display text-2xl text-simba-dark mb-6">Common Questions</h2>
               <div className="bg-gray-50 p-6 rounded-3xl">
                  {product.faqs && product.faqs.map((faq, i) => (
                     <FAQItem key={i} {...faq} />
                  ))}
               </div>
            </div>
            
         </div>
      </div>
    </div>
  );
};
